import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';

const URL_PARAM_KEYS = {
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  EXPERIENCE_TITLE: 'experienceTitle',
  UNITS: 'units',
  EXPERIENCES: 'experiences',
  SESSION_TITLE: 'sessionTitle',
  SESSIONS: 'sessions',
  GRANULARITY: 'granularity',
} as const;

const parseYmd = (s: string): Date | undefined => {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return undefined;
  const y = parseInt(m[1], 10);
  const mon = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  const dt = new Date(y, mon, d);
  return isNaN(dt.getTime()) ? undefined : dt;
};

export const urlSync = {
  saveFiltersToUrl: (filters: BaseAnalyticsFilter): void => {
    if (typeof window === 'undefined') return;

    try {
      const searchParams = new URLSearchParams(window.location.search);

      Object.values(URL_PARAM_KEYS).forEach(param => {
        searchParams.delete(param);
      });

      const toYmd = (d: Date): string => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      if (filters.startDate) {
        searchParams.set(URL_PARAM_KEYS.START_DATE, toYmd(filters.startDate));
      }

      if (filters.endDate) {
        searchParams.set(URL_PARAM_KEYS.END_DATE, toYmd(filters.endDate));
      }

      // Set experience/session title (maintain API compatibility while using UI-friendly URL)
      if (filters.experienceTitle?.trim()) {
        searchParams.set(URL_PARAM_KEYS.SESSION_TITLE, filters.experienceTitle);
      }

      if (filters.unitIds?.length) {
        const uniqueSorted = Array.from(new Set(filters.unitIds)).sort((a, b) => a - b);
        searchParams.set(URL_PARAM_KEYS.UNITS, uniqueSorted.join(','));
      }

      if (filters.experienceIds?.length) {
        const uniqueSorted = Array.from(new Set(filters.experienceIds)).sort((a, b) => a - b);
        searchParams.set(URL_PARAM_KEYS.SESSIONS, uniqueSorted.join(','));
      }

      if (filters.granularity) {
        searchParams.set(URL_PARAM_KEYS.GRANULARITY, filters.granularity);
      }

      const qs = searchParams.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save filters to URL:', error);
      }
    }
  },

  loadFiltersFromUrl: (): Partial<BaseAnalyticsFilter> => {
    if (typeof window === 'undefined') return {};

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlFilters: Partial<BaseAnalyticsFilter> = {};

      const startDate = urlParams.get(URL_PARAM_KEYS.START_DATE);
      if (startDate) {
        const date = parseYmd(startDate);
        if (date) urlFilters.startDate = date;
      }

      const endDate = urlParams.get(URL_PARAM_KEYS.END_DATE);
      if (endDate) {
        const date = parseYmd(endDate);
        if (date) urlFilters.endDate = date;
      }

      const experienceTitle =
        urlParams.get(URL_PARAM_KEYS.EXPERIENCE_TITLE) ||
        urlParams.get(URL_PARAM_KEYS.SESSION_TITLE);
      if (experienceTitle) {
        urlFilters.experienceTitle = experienceTitle;
      }

      const units = urlParams.get(URL_PARAM_KEYS.UNITS);
      if (units) {
        const unitIds = Array.from(
          new Set(
            units
              .split(',')
              .map(id => parseInt(id.trim(), 10))
              .filter(id => !isNaN(id)),
          ),
        ).sort((a, b) => a - b);
        if (unitIds.length > 0) {
          urlFilters.unitIds = unitIds;
        }
      }

      const experiences =
        urlParams.get(URL_PARAM_KEYS.EXPERIENCES) || urlParams.get(URL_PARAM_KEYS.SESSIONS);
      if (experiences) {
        const experienceIds = Array.from(
          new Set(
            experiences
              .split(',')
              .map(id => parseInt(id.trim(), 10))
              .filter(id => !isNaN(id)),
          ),
        ).sort((a, b) => a - b);
        if (experienceIds.length > 0) {
          urlFilters.experienceIds = experienceIds;
        }
      }

      const granularity = urlParams.get(URL_PARAM_KEYS.GRANULARITY);
      if (granularity === 'day' || granularity === 'month') {
        urlFilters.granularity = granularity;
      }

      return urlFilters;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load filters from URL:', error);
      }
      return {};
    }
  },

  hasUrlFilters: (): boolean => {
    if (typeof window === 'undefined') return false;

    try {
      const urlParams = new URLSearchParams(window.location.search);

      const oldParams = [
        URL_PARAM_KEYS.EXPERIENCE_TITLE,
        URL_PARAM_KEYS.EXPERIENCES,
        URL_PARAM_KEYS.UNITS,
        URL_PARAM_KEYS.START_DATE,
        URL_PARAM_KEYS.END_DATE,
        URL_PARAM_KEYS.GRANULARITY,
      ];
      const newParams = [URL_PARAM_KEYS.SESSION_TITLE, URL_PARAM_KEYS.SESSIONS];

      return [...oldParams, ...newParams].some(key => urlParams.has(key));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to check URL filters:', error);
      }
      return false;
    }
  },

  shouldPreserveFilters: (): boolean => {
    return urlSync.hasUrlFilters();
  },
};
